import type { Workflow } from '@botpress/client'
import { create } from 'zustand'
import { subscribeWithSelector, persist, createJSONStorage } from 'zustand/middleware'
import { getApiClient } from './clientsStore'

// Terminal/stop states
const TERMINAL = new Set<Workflow['status']>(['cancelled', 'completed', 'failed', 'timedout'])

type WorkflowState = {
  workflows: Record<string, Workflow>
  // persist this so we can rebuild clients after refresh:
  contexts: Record<string, { botId: string; workspaceId: string }>

  defaultPollMs: number
  pollers: Record<string, number>
}

type WorkflowActions = {
  register: (params: { botId: string; workspaceId: string; workflow: Workflow }) => void
  onStatusChange: (id: string, cb: (status: Workflow['status'], prev?: Workflow['status']) => void) => () => void
  awaitStatus: (
    id: string,
    desired: Workflow['status'] | Workflow['status'][],
    opts?: { timeoutMs?: number; signal?: AbortSignal }
  ) => Promise<Workflow>

  setDefaultPollMs: (ms: number) => void
  startPolling: (args: { workflowId: string; workspaceId: string; botId: string }) => void
  stopPolling: (id: string) => void

  _updateFromRemote: (wf: Workflow) => void
}

export const useWorkflowStore = create<WorkflowState & WorkflowActions>()(
  persist(
    subscribeWithSelector((set, get, api) => ({
      workflows: {},
      contexts: {}, // <-- added
      defaultPollMs: 1500,
      pollers: {},

      register: ({ botId, workspaceId, workflow }) => {
        // upsert local record + context
        set((state) => {
          const already = !!state.workflows[workflow.id]
          const next = {
            workflows: already ? state.workflows : { ...state.workflows, [workflow.id]: workflow },
            contexts: { ...state.contexts, [workflow.id]: { botId, workspaceId } },
          }
          return next
        })
        // start polling immediately (idempotent)
        get().startPolling({ workflowId: workflow.id, workspaceId, botId })
      },

      onStatusChange: (id, cb) => {
        const current = get().workflows[id]
        if (!current) throw new Error(`Workflow ${id} is not registered; cannot subscribe to status changes.`)
        return api.subscribe(
          (s: WorkflowState) => s.workflows[id]!.status,
          (status: Workflow['status'], prev: Workflow['status']) => cb(status, prev)
        )
      },

      awaitStatus: (id, desired, opts) => {
        const desiredSet = new Set(Array.isArray(desired) ? desired : [desired])
        const current = get().workflows[id]
        if (!current) return Promise.reject(new Error(`Workflow ${id} is not registered; cannot await status.`))
        if (desiredSet.has(current.status)) return Promise.resolve(current)

        return new Promise<Workflow>((resolve, reject) => {
          let done = false
          const cleanup: Array<() => void> = []
          const finish = (wf: Workflow, isTimeout = false) => {
            if (done) return
            done = true
            cleanup.forEach((fn) => fn())
            if (isTimeout)
              reject(new Error(`Timed out waiting for workflow ${id} to reach ${[...desiredSet].join('|')}`))
            else resolve(wf)
          }
          const unsub = api.subscribe(
            (s: WorkflowState) => s.workflows[id],
            (wf?: Workflow) => {
              if (wf && desiredSet.has(wf.status)) finish(wf)
            }
          )
          cleanup.push(unsub)
          if (opts?.timeoutMs && Number.isFinite(opts.timeoutMs)) {
            const t = setTimeout(() => finish(get().workflows[id]!, true), opts.timeoutMs)
            cleanup.push(() => clearTimeout(t))
          }
          if (opts?.signal) {
            if (opts.signal.aborted) {
              cleanup.forEach((fn) => fn())
              return reject(new Error('Aborted'))
            }
            const onAbort = () => {
              cleanup.forEach((fn) => fn())
              reject(new Error('Aborted'))
            }
            opts.signal.addEventListener('abort', onAbort, { once: true })
            cleanup.push(() => opts.signal?.removeEventListener('abort', onAbort))
          }
        })
      },

      setDefaultPollMs: (ms) => set({ defaultPollMs: Math.max(100, ms | 0 || 1500) }),

      startPolling: ({ workflowId, workspaceId, botId }) => {
        if (get().pollers[workflowId]) return // idempotent
        const apiClient = getApiClient({ botId, workspaceId })
        const pollMs = get().defaultPollMs
        const pid = setInterval(async () => {
          try {
            const wf = (await apiClient.getWorkflow({ id: workflowId })).workflow
            api.getState()._updateFromRemote(wf)
            if (TERMINAL.has(wf.status)) get().stopPolling(workflowId)
          } catch {
            get().stopPolling(workflowId)
          }
        }, pollMs) as unknown as number
        set((s) => ({ pollers: { ...s.pollers, [workflowId]: pid } }))
      },

      stopPolling: (id) =>
        set((s) => {
          const pid = s.pollers[id]
          if (pid) clearInterval(pid)
          const { [id]: _, ...rest } = s.pollers
          return { pollers: rest }
        }),

      _updateFromRemote: (wf) => {
        set((s) => {
          const prev = s.workflows[wf.id]
          const next: Workflow = prev ? { ...prev, ...wf } : wf
          if (TERMINAL.has(next.status)) {
            const pid = s.pollers[next.id]
            if (pid) clearInterval(pid)
            const { [next.id]: __, ...rest } = s.pollers
            return { workflows: { ...s.workflows, [next.id]: next }, pollers: rest }
          }
          return { workflows: { ...s.workflows, [next.id]: next } }
        })
      },
    })),
    {
      name: 'bp-workflows',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      // Only persist what we need to resume:
      partialize: (s) => ({
        workflows: s.workflows,
        contexts: s.contexts,
        defaultPollMs: s.defaultPollMs,
      }),
      // When storage is ready, restart polling for non-terminal workflows:
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) return
        queueMicrotask(() => {
          const { workflows, contexts } = useWorkflowStore.getState()
          for (const [id, wf] of Object.entries(workflows)) {
            if (!TERMINAL.has(wf.status)) {
              const ctx = contexts[id]
              if (ctx) {
                useWorkflowStore.getState().startPolling({
                  workflowId: id,
                  workspaceId: ctx.workspaceId,
                  botId: ctx.botId,
                })
              }
            }
          }
        })
      },
    }
  )
)
